import axios from 'axios';
import { deleteData, encodeSegment, getData, patchData, postData, putData } from './api';
import type {
  NutritionDayEntry,
  NutritionFoodDatabaseEntry,
  NutritionFoodEntry,
  NutritionWorkspaceData,
} from '../types/nutrition';

export interface NutritionLabelScanResult {
  name: string;
  servingSizeText: string | null;
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
}

export interface NutritionMealEstimateResult {
  name: string;
  servingCount: number;
  servingUnit: string;
  servingSizeText: string | null;
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
}

export interface NutritionMutationVersion {
  version: number;
}

type NutritionVersionPayload = {
  version: number;
};

type NutritionDayMutationEntry = Pick<NutritionDayEntry, 'targets' | 'updatedAt'>;

export type NutritionWorkspaceMutationOperation =
  | { kind: 'deleteDay'; dateKey: string }
  | { kind: 'upsertDay'; dateKey: string; entry: NutritionDayMutationEntry }
  | { kind: 'deleteFood'; foodId: string }
  | { kind: 'createFood'; dateKey: string; food: NutritionFoodEntry }
  | { kind: 'updateFood'; foodId: string; food: NutritionFoodEntry }
  | { kind: 'deleteSavedFood'; foodId: string }
  | { kind: 'createSavedFood'; food: NutritionFoodDatabaseEntry }
  | { kind: 'updateSavedFood'; foodId: string; food: NutritionFoodDatabaseEntry };

export type NutritionWorkspaceMutationPayload = NutritionVersionPayload & {
  operations: NutritionWorkspaceMutationOperation[];
};

type NutritionDayPayload = NutritionVersionPayload & Pick<NutritionDayEntry, 'targets' | 'updatedAt'>;
type NutritionFoodPayload = NutritionVersionPayload & {
  food: NutritionFoodEntry;
};
type NutritionSavedFoodPayload = NutritionVersionPayload & {
  food: NutritionFoodDatabaseEntry;
};

const getLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getNutritionMutationConfig = () => ({
  headers: {
    'X-Nutrition-Today-Date-Key': getLocalDateKey(new Date()),
  },
});

export const nutritionApi = {
  loadWorkspace(): Promise<NutritionWorkspaceData> {
    return getData<NutritionWorkspaceData>('/api/nutrition/workspace');
  },

  replaceWorkspace(workspace: NutritionWorkspaceData): Promise<NutritionWorkspaceData> {
    return putData<NutritionWorkspaceData, NutritionWorkspaceData>(
      '/api/nutrition/workspace',
      workspace,
      getNutritionMutationConfig(),
    );
  },

  applyWorkspaceMutations(payload: NutritionWorkspaceMutationPayload): Promise<NutritionWorkspaceData> {
    return patchData<NutritionWorkspaceData, NutritionWorkspaceMutationPayload>(
      '/api/nutrition/workspace',
      payload,
      getNutritionMutationConfig(),
    );
  },

  upsertDay(dateKey: string, payload: NutritionDayPayload): Promise<NutritionMutationVersion> {
    return putData<NutritionMutationVersion, NutritionDayPayload>(
      `/api/nutrition/days/${encodeSegment(dateKey)}`,
      payload,
      getNutritionMutationConfig(),
    );
  },

  deleteDay(dateKey: string, version: number): Promise<NutritionMutationVersion> {
    return deleteData<NutritionMutationVersion>(`/api/nutrition/days/${encodeSegment(dateKey)}`, {
      data: { version },
      ...getNutritionMutationConfig(),
    });
  },

  createFood(dateKey: string, payload: NutritionFoodPayload): Promise<NutritionMutationVersion> {
    return postData<NutritionMutationVersion, NutritionFoodPayload>(
      `/api/nutrition/days/${encodeSegment(dateKey)}/foods`,
      payload,
    );
  },

  updateFood(foodId: string, payload: NutritionFoodPayload): Promise<NutritionMutationVersion> {
    return patchData<NutritionMutationVersion, NutritionFoodPayload>(
      `/api/nutrition/foods/${encodeSegment(foodId)}`,
      payload,
    );
  },

  deleteFood(foodId: string, version: number): Promise<NutritionMutationVersion> {
    return deleteData<NutritionMutationVersion>(`/api/nutrition/foods/${encodeSegment(foodId)}`, {
      data: { version },
    });
  },

  createSavedFood(payload: NutritionSavedFoodPayload): Promise<NutritionMutationVersion> {
    return postData<NutritionMutationVersion, NutritionSavedFoodPayload>('/api/nutrition/saved-foods', payload);
  },

  updateSavedFood(foodId: string, payload: NutritionSavedFoodPayload): Promise<NutritionMutationVersion> {
    return patchData<NutritionMutationVersion, NutritionSavedFoodPayload>(
      `/api/nutrition/saved-foods/${encodeSegment(foodId)}`,
      payload,
    );
  },

  deleteSavedFood(foodId: string, version: number): Promise<NutritionMutationVersion> {
    return deleteData<NutritionMutationVersion>(`/api/nutrition/saved-foods/${encodeSegment(foodId)}`, {
      data: { version },
    });
  },

  async scanLabel(file: File): Promise<NutritionLabelScanResult> {
    const formData = new FormData();
    formData.append('image', file);

    try {
      return await postData<NutritionLabelScanResult, FormData>('/api/nutrition/label-scan', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          typeof error.response?.data?.error === 'string'
            ? error.response.data.error
            : error.message;

        throw new Error(message);
      }

      throw error instanceof Error ? error : new Error('Unable to read that nutrition label.');
    }
  },

  async estimateMeal(file: File): Promise<NutritionMealEstimateResult> {
    const formData = new FormData();
    formData.append('image', file);

    try {
      return await postData<NutritionMealEstimateResult, FormData>('/api/nutrition/meal-estimate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          typeof error.response?.data?.error === 'string'
            ? error.response.data.error
            : error.message;

        throw new Error(message);
      }

      throw error instanceof Error ? error : new Error('Unable to estimate that meal photo.');
    }
  },
};

export default nutritionApi;
